/**
 * FeedbackPass — Three.js post-process that adds a MilkDrop-style
 * frame-feedback loop to the rendering pipeline.
 *
 * Algorithm (per frame):
 *   1. Read tDiffuse — the current scene + bloom output for this frame.
 *   2. Sample tPrev (the rendered output from the PREVIOUS frame) using
 *      a per-pixel warp:  zoom (radial pull-in/out) + rotation + drift.
 *   3. Output = max(current, previousWarped * feedbackStrength)
 *      with a soft luminance ceiling so black backgrounds stay black and
 *      trails fade instead of accumulating to a white wash.
 *   4. Save the output back into tPrev for the next frame.
 *
 * That feedback loop is what gives MilkDrop its hypnotic "smoke / liquid
 * history" feel. Every pixel "remembers" where it was, so motion leaves
 * trails that drift, swirl, and dissolve organically.
 *
 * Disabled (passthrough) when feedbackStrength <= 0, so users who don't
 * like the trail look pay zero perf cost.
 */

import * as THREE from "three";
import { Pass, FullScreenQuad } from "three/examples/jsm/postprocessing/Pass.js";

/**
 * Public knobs that drive the feedback look. All are mirrored into the
 * shader uniforms each frame so the renderer can tweak them live.
 */
export interface FeedbackPassParams {
  /** 0..0.99 — how much of last frame to mix in. 0 = no trails, 0.95 = heavy. */
  strength: number;
  /** -0.05..0.05 — radial zoom per frame. + zooms IN, - zooms OUT. */
  zoom: number;
  /** -0.1..0.1 — rotation (radians) per frame applied to the previous frame. */
  rotate: number;
  /** -0.02..0.02 — horizontal drift per frame. */
  driftX: number;
  /** -0.02..0.02 — vertical drift per frame. */
  driftY: number;
  /** 0..1 — multiplied into the warped color so trails dim each frame. */
  decay: number;
}

export const DEFAULT_FEEDBACK: FeedbackPassParams = {
  strength: 0,
  zoom: 0,
  rotate: 0,
  driftX: 0,
  driftY: 0,
  decay: 0.96,
};

const VERTEX_SHADER = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position, 1.0);
  }
`;

const FRAGMENT_SHADER = /* glsl */ `
  precision highp float;

  uniform sampler2D tDiffuse;
  uniform sampler2D tPrev;
  uniform float uStrength;
  uniform float uZoom;
  uniform float uRotate;
  uniform vec2  uDrift;
  uniform float uDecay;

  varying vec2 vUv;

  void main() {
    // ---- Sample the current scene/bloom output ----
    vec4 cur = texture2D(tDiffuse, vUv);

    // ---- Compute the warp transform for the previous frame ----
    // Center coords on (0,0) so zoom + rotate operate around screen center.
    vec2 c = vUv - 0.5;
    // Apply zoom by scaling the sample coords (note: positive uZoom shrinks
    // the source coord -> samples a smaller area -> visually appears as a
    // zoom IN of the previous frame).
    c *= 1.0 - uZoom;
    // Apply rotation.
    float s = sin(uRotate);
    float co = cos(uRotate);
    c = mat2(co, -s, s, co) * c;
    // Re-center + apply drift.
    vec2 prevUv = c + 0.5 + uDrift;

    // Sample the previous frame; clamp to [0,1] so out-of-bounds reads
    // are black (creates a clean fade at edges instead of mirror artifacts).
    vec4 prev = vec4(0.0);
    if (prevUv.x >= 0.0 && prevUv.x <= 1.0 && prevUv.y >= 0.0 && prevUv.y <= 1.0) {
      prev = texture2D(tPrev, prevUv) * uDecay;
    }

    // ---- Composite current + warped/decayed previous ----
    // Avoid additive accumulation: it turns black scenes grey/white after a
    // few seconds. max() keeps the current frame crisp while the warped history
    // contributes only where it is still brighter than the background.
    vec3 trail = prev.rgb * uStrength;
    vec3 mixed = max(cur.rgb, trail);
    float lum = dot(mixed, vec3(0.2126, 0.7152, 0.0722));
    if (lum > 0.92) {
      mixed *= 0.92 / lum;
    }
    gl_FragColor = vec4(clamp(mixed, 0.0, 1.0), max(cur.a, prev.a * uStrength));
  }
`;

export class FeedbackPass extends Pass {
  /** Render targets we ping-pong between for the feedback loop. */
  private rtA: THREE.WebGLRenderTarget;
  private rtB: THREE.WebGLRenderTarget;
  /** Which target holds the "previous frame" right now. */
  private which: "A" | "B" = "A";
  /** Full-screen quad that runs our shader. */
  private fsQuad: FullScreenQuad;
  /** Material with our feedback uniforms. */
  private material: THREE.ShaderMaterial;

  /** Live params — mutate directly each frame. */
  public params: FeedbackPassParams = { ...DEFAULT_FEEDBACK };

  constructor(width: number, height: number) {
    super();

    // Two ping-pong targets so we can read from one while writing the other.
    // Float type would give us HDR feedback but eats double memory; using
    // standard 8-bit RGBA is fine because we clamp at 1.0 in the shader.
    const rtOpts: THREE.RenderTargetOptions = {
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      format: THREE.RGBAFormat,
      type: THREE.UnsignedByteType,
      depthBuffer: false,
      stencilBuffer: false,
    };
    this.rtA = new THREE.WebGLRenderTarget(width, height, rtOpts);
    this.rtB = new THREE.WebGLRenderTarget(width, height, rtOpts);

    this.material = new THREE.ShaderMaterial({
      uniforms: {
        tDiffuse: { value: null },
        tPrev: { value: this.rtA.texture },
        uStrength: { value: 0 },
        uZoom: { value: 0 },
        uRotate: { value: 0 },
        uDrift: { value: new THREE.Vector2(0, 0) },
        uDecay: { value: 0.96 },
      },
      vertexShader: VERTEX_SHADER,
      fragmentShader: FRAGMENT_SHADER,
      depthTest: false,
      depthWrite: false,
    });
    this.fsQuad = new FullScreenQuad(this.material);
  }

  /** Resize ping-pong targets when the canvas size changes. */
  setSize(width: number, height: number): void {
    this.rtA.setSize(width, height);
    this.rtB.setSize(width, height);
  }

  /**
   * The Pass interface contract. EffectComposer calls this every frame
   * with the current input (`readBuffer`) and where to write our output
   * (`writeBuffer`). We also write a copy into the next ping-pong target
   * so that the NEXT frame can read it as its "previous."
   */
  render(
    renderer: THREE.WebGLRenderer,
    writeBuffer: THREE.WebGLRenderTarget,
    readBuffer: THREE.WebGLRenderTarget,
  ): void {
    // Sync uniforms from the live params.
    const u = this.material.uniforms;
    u.tDiffuse.value = readBuffer.texture;
    u.uStrength.value = this.params.strength;
    u.uZoom.value = this.params.zoom;
    u.uRotate.value = this.params.rotate;
    (u.uDrift.value as THREE.Vector2).set(this.params.driftX, this.params.driftY);
    u.uDecay.value = this.params.decay;
    // The PREVIOUS frame is whichever ping-pong target was last written to.
    u.tPrev.value = (this.which === "A" ? this.rtA : this.rtB).texture;

    // We need to write the composited result to TWO places:
    //   (a) the EffectComposer's `writeBuffer` so the next pass / screen
    //       can consume it
    //   (b) the OTHER ping-pong target so next frame can read it back
    //       as the new "previous frame"
    // Both writes are the same pixels, so we just render twice.
    const target = this.which === "A" ? this.rtB : this.rtA;
    renderer.setRenderTarget(target);
    renderer.clear();
    this.fsQuad.render(renderer);

    // Now write to the EffectComposer's writeBuffer (or the canvas if
    // this is the final pass and renderToScreen is true).
    renderer.setRenderTarget(this.renderToScreen ? null : writeBuffer);
    if (this.clear) renderer.clear();
    // Re-render the same shader; sampling the SAME prev so the result
    // is deterministic. (Could also `copyTexture`, but a single extra
    // shader invocation is cheaper than the WebGL copy plumbing.)
    this.fsQuad.render(renderer);

    // Flip ping-pong for next frame.
    this.which = this.which === "A" ? "B" : "A";
  }

  /** Free GPU resources. Currently unused — kept for future cleanup paths. */
  dispose(): void {
    this.rtA.dispose();
    this.rtB.dispose();
    this.material.dispose();
    this.fsQuad.dispose();
  }
}
